import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextInput } from '@tremor/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { supabase } from '../../commons/config/settings';
import PaparazzoLogo from '../../commons/images/svgs/paparazzo_logo_vertical.svg';
import ManzoniLogo from '../../commons/images/manzoni_logo.png';
import '../login/Login.css';

// Password input wrapper component with visibility toggle
const PasswordInput = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  marginTop = 'mt-0',
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  marginTop?: string;
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Create masked value for display
  const displayValue = showPassword ? value : value.replace(/./g, '•');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (showPassword) {
      // Normal behavior when password is visible
      onChange(e);
    } else {
      // Handle masked input
      const inputValue = e.target.value;
      const currentLength = value.length;
      const newLength = inputValue.length;

      let newValue = value;

      if (newLength > currentLength) {
        // Adding character
        const addedChar = inputValue.slice(-1);
        if (addedChar !== '•') {
          newValue = value + addedChar;
        }
      } else if (newLength < currentLength) {
        // Removing character
        newValue = value.slice(0, -1);
      }

      // Create synthetic event
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue,
        },
      };

      onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div
      className="input-container"
      style={{ marginTop: marginTop === 'mt-4' ? '1rem' : '0' }}
    >
      <TextInput
        name="password"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        icon={undefined}
        error={false}
        errorMessage=""
        disabled={disabled}
        maxWidth="max-w-none"
        marginTop="mt-0"
      />
      <button
        type="button"
        onClick={togglePasswordVisibility}
        className="input-icon"
        onMouseEnter={(e) => (e.currentTarget.style.color = '#374151')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#6b7280')}
        title={showPassword ? 'Nascondi password' : 'Mostra password'}
      >
        {showPassword ? (
          <EyeSlashIcon style={{ width: '20px', height: '20px' }} />
        ) : (
          <EyeIcon style={{ width: '20px', height: '20px' }} />
        )}
      </button>
    </div>
  );
};

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    // Verifica il token dall'URL se presente
    const verifyToken = async () => {
      const token = searchParams.get('token_hash');
      const type = searchParams.get('type');

      if (token && (type === 'invite' || type === 'recovery')) {
        try {
          setIsVerifying(true);

          console.log('Verifying token:', token, 'type:', type);

          // Per il tipo 'invite' e 'recovery', usa sempre token_hash
          const verificationResult = await supabase.auth.verifyOtp({
            token_hash: token,
            type: type as 'invite' | 'recovery',
          });

          if (verificationResult.error) {
            console.error(
              'Token verification error:',
              verificationResult.error,
            );
            toast.error(
              "Link non valido o scaduto. Contatta l'amministratore.",
            );
            return;
          }

          if (verificationResult.data?.user) {
            setUserEmail(verificationResult.data.user.email || '');
            setIsVerified(true);
            console.log('Token verified successfully');
          }
        } catch (err) {
          console.error('Error verifying token:', err);
          toast.error(
            "Errore nella verifica del link. Contatta l'amministratore.",
          );
        } finally {
          setIsVerifying(false);
        }
      } else {
        // Se non ci sono parametri, verifica la sessione esistente
        const checkAuth = async () => {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) {
            toast.error(
              "Link non valido o scaduto. Contatta l'amministratore.",
            );
            return;
          }

          if (session.user?.email) {
            setUserEmail(session.user.email);
            setIsVerified(true);
          }
        };

        checkAuth();
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validazioni
    if (!password || !confirmPassword) {
      toast.error('Compila tutti i campi');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      toast.success('Password impostata con successo!');
      setSuccess(true);

      // Redirect al dashboard dopo 2 secondi
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error("Errore nell'impostazione della password:", err);
      toast.error("Errore nell'impostazione della password. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  // Mostra loading durante la verifica
  if (isVerifying) {
    return (
      <div className="login">
        <form className="form">
          <div className="form__header">
            <img
              src={PaparazzoLogo}
              alt="Paparazzo"
              className="app-logo"
              decoding="async"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26.835"
              height="22.215"
              viewBox="0 0 26.835 22.215"
            >
              <g
                id="Raggruppa_452"
                data-name="Raggruppa 452"
                transform="translate(-696.392 -178.392)"
              >
                <line
                  id="Linea_45"
                  data-name="Linea 45"
                  x2="22.62"
                  y2="18"
                  transform="translate(698.5 180.5)"
                  fill="none"
                  stroke="#c8dbea"
                  strokeLinecap="round"
                  strokeWidth="3"
                />
                <line
                  id="Linea_46"
                  data-name="Linea 46"
                  x2="18"
                  y2="22.62"
                  transform="translate(721.12 180.5) rotate(90)"
                  fill="none"
                  stroke="#c8dbea"
                  strokeLinecap="round"
                  strokeWidth="3"
                />
              </g>
            </svg>
            <img
            src={ManzoniLogo}
            alt="AMC Manzoni"
            className="user-logo"
            decoding="async"
          />
          </div>
          <div className="form__body">
            <span className="h1">Verifica in corso...</span>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              Verifica del link di invito in corso...
            </p>
          </div>
        </form>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login">
        <form className="form">
          <div className="form__header">
            <img
              src={PaparazzoLogo}
              alt="Paparazzo"
              className="app-logo"
              decoding="async"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26.835"
              height="22.215"
              viewBox="0 0 26.835 22.215"
            >
              <g
                id="Raggruppa_452"
                data-name="Raggruppa 452"
                transform="translate(-696.392 -178.392)"
              >
                <line
                  id="Linea_45"
                  data-name="Linea 45"
                  x2="22.62"
                  y2="18"
                  transform="translate(698.5 180.5)"
                  fill="none"
                  stroke="#c8dbea"
                  strokeLinecap="round"
                  strokeWidth="3"
                />
                <line
                  id="Linea_46"
                  data-name="Linea 46"
                  x2="18"
                  y2="22.62"
                  transform="translate(721.12 180.5) rotate(90)"
                  fill="none"
                  stroke="#c8dbea"
                  strokeLinecap="round"
                  strokeWidth="3"
                />
              </g>
            </svg>
            <img
            src={ManzoniLogo}
            alt="AMC Manzoni"
            className="user-logo"
            decoding="async"
          />
          </div>
          <div className="form__body">
            <span className="h1">Password Impostata!</span>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              La tua password è stata impostata con successo.
              <br />
              Stai per essere reindirizzato al login...
            </p>
          </div>
        </form>
      </div>
    );
  }

  // Mostra il form solo se verificato
  if (!isVerified) {
    return null;
  }

  const userMessage = searchParams.get('type') === 'invite' ? 'Imposta la tua password per completare la registrazione.' : 'Imposta la tua password per completare il recupero.';

  return (
    <div className="login">
      <form className="form" onSubmit={handleSetPassword}>
        <div className="form__header">
          <img src={PaparazzoLogo} alt="Paparazzo" className="app-logo" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26.835"
            height="22.215"
            viewBox="0 0 26.835 22.215"
          >
            <g
              id="Raggruppa_452"
              data-name="Raggruppa 452"
              transform="translate(-696.392 -178.392)"
            >
              <line
                id="Linea_45"
                data-name="Linea 45"
                x2="22.62"
                y2="18"
                transform="translate(698.5 180.5)"
                fill="none"
                stroke="#c8dbea"
                strokeLinecap="round"
                strokeWidth="3"
              />
              <line
                id="Linea_46"
                data-name="Linea 46"
                x2="18"
                y2="22.62"
                transform="translate(721.12 180.5) rotate(90)"
                fill="none"
                stroke="#c8dbea"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </g>
          </svg>
          <img
            src={ManzoniLogo}
            alt="AMC Manzoni"
            className="user-logo"
            decoding="async"
          />
        </div>
        <div className="form__body">
          <span className="h1">Imposta Password</span>

          {userEmail && (
            <p
              style={{
                textAlign: 'center',
                marginBottom: '16px',
                color: '#666',
              }}
            >
              Benvenuto! {userMessage}
              <strong style={{ marginTop: '12px', display: 'block' }}>
                {userEmail}
              </strong>
            </p>
          )}

          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nuova password"
            disabled={loading}
            marginTop="mt-0"
          />

          <PasswordInput
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Conferma password"
            disabled={loading}
            marginTop="mt-4"
          />

          <button
            className="cta"
            type="submit"
            disabled={loading}
            style={{ opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Impostazione...' : 'Imposta Password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SetPassword;
